// ...existing code...
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { ChatService } from '../../features/chat/services/chat.service';
import { API_URL, OPENAI_API_KEY, ANTHROPIC_API_KEY, CEREBRAS_API_KEY, emails, ENVIRONMENT, AES_SECRET_KEY } from '../../config';
import { decryptKey, importAesKey } from '../utils/crypto-util';
import { MetricsService } from './metrics.service';
import { Cerebras } from '@cerebras/cerebras_cloud_sdk';
import OpenAI from 'openai';
import { ToastService } from './toast.service';
import { OpenAiService } from './open-ai.service';

export interface Message {
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  copied?: boolean;
  showDiff?: boolean;
  previousText?: string;
  id?: string; // Unique ID for tracking
}
export interface PromptMemory {
  id: string;
  prompt: string;
  name: string;
  consultantName?: string;
  presentRole?: string;
  interviewingRole?: string;
  interviewingClient?: string;
  presentClient?: string;
  previousClient?: string;
  interviewType?: string;
  jobDescription?: string;
}
export interface QuestionDto {
  promptId: string;
  chatThreadId: string;
  question: string;
}

export interface SummaryResponse {
  title: string;
  summary: string;
}
export interface ChatHistory {
  s3Url: any;
  id: string;
  promptId: string;
  chatThreadId: string;
  question: string;
  answer: string;
  timestamp: string;
}

export interface aiKeyStatusDTO {
  id: string;
  error: string;
  aiService?: string;
  status: string;
  createdAT: Number;
}

export interface ApiKeys {
  openAiKey: string;
  claudeAiKey: string;
}

export interface ConversationState {
  topic: string;
  decisions: string[];
  commitments: string[];
  constraints: string[];
  stories: string[];
  numbers: Record<string, string>;
  pending: string;
  artifacts: string[];
}

const EMPTY_STATE: ConversationState = {
  topic: '',
  decisions: [],
  commitments: [],
  constraints: [],
  stories: [],
  numbers: {},
  pending: '',
  artifacts: []
};

@Injectable({
  providedIn: 'root'
})
export class OpenApiService {
  /**
   * Stream Claude answer using built context from frontend
   */

  serviceStatus: any;

  private openAiKey: string = '';
  private claudeAiKey: string = '';

  // ── Sticky Fallback: skip OpenAI for 8 hours after a failure ──
  private _openAiFallbackTimestamp: number = 0;
  private readonly _stickyFallbackDurationMs: number = 8 * 60 * 60 * 1000; // 8 hours

  // ── ISE (Incremental State Extraction) ──
  private _stateJSON: ConversationState = { ...EMPTY_STATE };
  private _extractionInFlightPromise: Promise<void> | null = null;
  private _extractedPairsCount: number = 0;
  private _currentPromptId: string = '';

  constructor(private http: HttpClient, private toast: ToastService, private openAiService: OpenAiService, private metricsService: MetricsService) { }

  /** Set current prompt ID and restore ISE state from localStorage */
  setPromptId(promptId: string): void {
    if (this._currentPromptId !== promptId) {
      this._currentPromptId = promptId;
      this._extractedPairsCount = 0;
      try {
        const stored = localStorage.getItem(`iseState_${promptId}`);
        const storedCount = localStorage.getItem(`iseStateCount_${promptId}`);
        if (stored) {
          this._stateJSON = JSON.parse(stored);
          this._extractedPairsCount = storedCount ? parseInt(storedCount, 10) : 0;
          console.log(`[ISE] Restored state for prompt ${promptId} (${stored.length} chars, ${this._extractedPairsCount} pairs)`);
        } else {
          this._stateJSON = { ...EMPTY_STATE };
          console.log(`[ISE] No stored state for prompt ${promptId} — starting fresh`);
        }
      } catch {
        this._stateJSON = { ...EMPTY_STATE };
      }
    }
  }

  /** Save ISE state to localStorage */
  private _persistState(): void {
    if (this._currentPromptId) {
      try {
        localStorage.setItem(`iseState_${this._currentPromptId}`, JSON.stringify(this._stateJSON));
        localStorage.setItem(`iseStateCount_${this._currentPromptId}`, String(this._extractedPairsCount));
      } catch { /* quota exceeded — ignore */ }
    }
  }

  /**
   * Incremental State Extraction via Cerebras llama3.1-8b.
   * Input: current state JSON + last 1 Q&A.
   * Output: JSON patch with new facts only.
   */
  private extractFacts(pair: { question: string; answer: string }): Promise<void> {
    if (this._extractionInFlightPromise) {
      console.log('[ISE] Extraction already in-flight — skipping');
      return this._extractionInFlightPromise;
    }

    const currentStateStr = JSON.stringify(this._stateJSON);

    const maxAnswerLen = 800;
    let truncatedAnswer = pair.answer;
    let wasAnswerTruncated = false;
    if (truncatedAnswer.length > maxAnswerLen) {
      wasAnswerTruncated = true;
      const chunk = truncatedAnswer.substring(0, maxAnswerLen);
      const lastSentenceEnd = Math.max(chunk.lastIndexOf('. '), chunk.lastIndexOf('.\n'), chunk.lastIndexOf('? '), chunk.lastIndexOf('! '));
      truncatedAnswer = lastSentenceEnd > maxAnswerLen * 0.5
        ? chunk.substring(0, lastSentenceEnd + 1)
        : chunk;
    }

    const cerebrasMessages: Array<{ role: 'system' | 'user'; content: string }> = [
      {
        role: 'system',
        content: `You extract NEW structured facts from a single Q&A exchange. You receive the current known state and one new exchange. Output ONLY a JSON object containing facts that are NOT already in the current state. If the exchange adds nothing new, output {}.

OUTPUT FORMAT (strict JSON, no markdown, no explanation):
{
  "topic": "updated topic if changed, omit if same",
  "decisions": ["only NEW decisions not already in state"],
  "commitments": ["only NEW things the candidate confirmed"],
  "constraints": ["only NEW limitations/preferences revealed"],
  "stories": ["only NEW behavioral/debugging stories, 1 sentence each"],
  "numbers": {"key": "value — only NEW numbers not already in state"},
  "pending": "if the answer was cut off or explicitly incomplete, describe what remains to be explained — otherwise omit or set empty string",
  "artifacts": ["description of any NEW code snippet, diagram, or architecture shared — e.g. 'binary search in Java (iterative, O(log n))' — do NOT include raw code, just describe it"]
}

RULES:
- ALWAYS set "topic" to the current question's subject (e.g. "Angular", "Java", "Kafka") — even if it seems similar to the previous topic. This is critical for topic-change detection.
- Omit any field where there is nothing new to add
- Each array item must be 1 concise sentence max
- Numbers: use exact values (50K TPS, not "high throughput")
- Do NOT repeat facts already in the current state
- pending: set to "" if the answer completed normally
- artifacts: only add when actual code blocks or ASCII diagrams were present
- Output valid JSON only, nothing else`
      },
      {
        role: 'user',
        content: `CURRENT STATE:\n${currentStateStr}\n\nNEW EXCHANGE:\nQ: ${pair.question}\nA: ${truncatedAnswer}\n\nWhat new facts emerged? Output JSON patch only.`
      }
    ];

    console.log(`[ISE] Extracting facts | State: ${currentStateStr.length} chars | Answer: ${pair.answer.length} chars (sent: ${truncatedAnswer.length}) | Q: "${pair.question.substring(0, 50)}..."`);

    this._extractionInFlightPromise = (async () => {
      try {
        const client = new Cerebras({ apiKey: CEREBRAS_API_KEY });
        const t0 = Date.now();

        const timeoutPromise = new Promise<null>((resolve) => setTimeout(() => resolve(null), 10000));
        const cerebrasPromise = client.chat.completions.create({
          model: 'llama3.1-8b',
          messages: cerebrasMessages,
          temperature: 0.7,
          max_tokens: 1024,
          stream: false,
        });
        const resp = await Promise.race([cerebrasPromise, timeoutPromise]);
        const elapsed = Date.now() - t0;

        if (!resp) {
          console.warn(`[ISE] Cerebras timed out after ${elapsed}ms — skipping`);
          return;
        }

        const raw = ((resp as any).choices?.[0]?.message?.content || '').trim();
        console.log(`[ISE] Cerebras responded in ${elapsed}ms | Raw: ${raw}`);

        if (!raw) {
          console.warn(`[ISE] Empty response from Cerebras (${elapsed}ms)`);
          return;
        }

        const patch = this._parseISEPatch(raw);
        if (wasAnswerTruncated && patch.pending) {
          console.log(`[ISE] Clearing false pending (answer was truncated, not interrupted)`);
          patch.pending = '';
        }
        this._mergeStatePatch(patch);
        this._persistState();

        console.log(`[ISE] State updated | ${JSON.stringify(this._stateJSON).length} chars`);
      } catch (err) {
        console.warn('[ISE] Extraction failed — state unchanged, will retry next turn', err);
      } finally {
        this._extractionInFlightPromise = null;
      }
    })();

    return this._extractionInFlightPromise;
  }

  /** Parse Cerebras JSON response, tolerating minor formatting issues */
  private _parseISEPatch(raw: string): Partial<ConversationState> {
    try {
      const cleaned = raw.replace(/^```json?\s*/i, '').replace(/\s*```$/i, '').trim();
      return JSON.parse(cleaned);
    } catch {
      console.warn('[ISE] Failed to parse patch JSON:', raw);
      return {};
    }
  }

  /** Merge extracted patch into existing state. Clears old facts on topic change to prevent bleeding. */
  private _mergeStatePatch(patch: Partial<ConversationState>): void {
    // Detect topic change — if the new topic is substantially different, clear transient facts
    // so previous question context doesn't bleed into the new answer.
    if (patch.topic && patch.topic.trim()) {
      const oldTopic = (this._stateJSON.topic || '').toLowerCase().trim();
      const newTopic = patch.topic.toLowerCase().trim();
      if (oldTopic && newTopic && oldTopic !== newTopic) {
        // Topics diverged — clear decisions and artifacts (keep stories/commitments as they are personal)
        console.log(`[ISE] Topic changed: "${oldTopic}" → "${newTopic}" — clearing transient facts`);
        this._stateJSON.decisions = [];
        this._stateJSON.artifacts = [];
        this._stateJSON.numbers = {};
        this._stateJSON.pending = '';
      }
      this._stateJSON.topic = patch.topic;
    }
    if (typeof patch.pending === 'string') {
      this._stateJSON.pending = patch.pending;
    }
    const arrayFields: Array<keyof Pick<ConversationState, 'decisions' | 'commitments' | 'constraints' | 'stories' | 'artifacts'>> =
      ['decisions', 'commitments', 'constraints', 'stories', 'artifacts'];
    for (const field of arrayFields) {
      if (Array.isArray(patch[field])) {
        for (const item of patch[field]!) {
          if (item && !this._stateJSON[field].includes(item)) {
            this._stateJSON[field].push(item);
          }
        }
        if (this._stateJSON[field].length > 15) {
          this._stateJSON[field] = this._stateJSON[field].slice(-15);
        }
      }
    }
    if (patch.numbers && typeof patch.numbers === 'object') {
      Object.assign(this._stateJSON.numbers, patch.numbers);
    }
  }

  /**
   * Background state extraction after each bot response.
   * On cold start (no state), catches up from last 3 pairs.
   * Otherwise processes only the LAST exchange.
   */
  async preExtractState(recentHistory: any[]): Promise<void> {
    if (!Array.isArray(recentHistory) || recentHistory.length === 0) return;
    const sorted = [...recentHistory].sort((a: any, b: any) =>
      (new Date(a.timestamp).getTime() || 0) - (new Date(b.timestamp).getTime() || 0)
    );
    const all = sorted
      .filter((c: any) => c.question && c.answer)
      .map((c: any) => ({ question: c.question, answer: c.answer }));

    if (all.length === 0) return;

    // Cold start: if state is empty but history exists, extract from last 3 pairs
    if (JSON.stringify(this._stateJSON) === JSON.stringify(EMPTY_STATE) && all.length > 1) {
      const catchUpPairs = all.slice(Math.max(0, all.length - 3));
      console.log(`[ISE] Cold start — extracting from last ${catchUpPairs.length} pairs`);
      for (const pair of catchUpPairs) {
        await this.extractFacts(pair);
      }
      this._extractedPairsCount = all.length;
      this._persistState();
      console.log(`[ISE] Cold start complete — state: ${JSON.stringify(this._stateJSON).length} chars`);
      return;
    }

    // Normal path: extract from LAST exchange only
    if (all.length <= this._extractedPairsCount) {
      console.log(`[ISE] Already extracted ${this._extractedPairsCount} pairs — skipping`);
      return;
    }

    const lastPair = all[all.length - 1];
    console.log(`[ISE] Extracting from last exchange | Total: ${all.length} | Already extracted: ${this._extractedPairsCount}`);
    await this.extractFacts(lastPair);
    this._extractedPairsCount = all.length;
    this._persistState();
    console.log(`[ISE] Extraction complete — covers ${this._extractedPairsCount} pairs`);
  }

  async streamClaudeAnswerWithContext(
    context: Array<{ role: string; content: string }>,
    botMessageIndex: number,
    chatService: any,
    onUpdate?: (partial: string) => void,
    abortSignal?: AbortSignal,
    model: string = 'claude-sonnet-4-5-20250929',
    interviewType?: string,
    skipStatusCheck: boolean = false,
    promptId?: string
  ): Promise<void> {
    if (!skipStatusCheck) {
      try {
        const statuses = this.serviceStatus;
        const svc = (s: any) => String(s?.aiService || s?.aiservice || '').toLowerCase();
        const claudeInactive = statuses.some((s: any) => svc(s) === 'claude' && String(s.status).toLowerCase() === 'inactive');
        if (claudeInactive) {
          try {
            await this.streamAskQuestionWithContext(context, botMessageIndex, chatService, onUpdate, abortSignal, 'gpt-5.2', interviewType, true, promptId);
            return;
          } catch (e) {
            const anyErr = e as any;
            if (anyErr?.name === 'AbortError' || anyErr?.code === 20 || anyErr?.type === 'aborted') {
              chatService.finalizeConversation();
              return;
            }
            console.error('Immediate fallback to OpenAI failed (Claude inactive):', e);
            chatService.updateBotMessage(botMessageIndex, "Sorry, Otter AI Assistant is currently unavailable. Please try again later.");
            chatService.finalizeConversation();
            return;
          }
        }
      } catch (e) {
        console.warn('Could not check Claude status before request, proceeding with Claude attempt', e);
      }
    }
    let retryCount = 0;
    const maxRetries = 3;

    const recordErrorAndNotify = async (errorCode: string, errorMessage: string, statusCode?: string | number) => {
      await this.upsertAiKeyStatus('claude', errorCode, errorMessage, statusCode);
      await this.upsertAiKeysStatus('claude', errorCode, errorMessage, statusCode);
    };

    // Build prompt-caching payload: system blocks with cache_control + conversation messages
    const systemMsgs = context.filter(m => m.role === 'system');
    const convoMsgs = context.filter(m => m.role !== 'system').map(m => ({ role: m.role, content: m.content }));
    const systemBlocks = systemMsgs.map((m, i) => {
      const block: any = { type: 'text', text: m.content };
      if (i === 0) block.cache_control = { type: 'ephemeral' };
      return block;
    });

    const metricsStart = performance.now();
    let metricsTtfb = 0;
    let metricsFirstToken = 0;
    let metricsRecorded = false;

    const userQuestion = [...context].reverse().find(m => m.role === 'user')?.content || '';
    const recordMetric = (status: string, errorCode?: string) => {
      if (metricsRecorded) return;
      metricsRecorded = true;
      this.metricsService.record({
        provider: 'anthropic',
        model,
        interviewType: interviewType || '',
        promptId: promptId || '',
        question: userQuestion,
        ttfbMs: Math.round(metricsTtfb),
        firstTokenMs: Math.round(metricsFirstToken),
        totalDurationMs: Math.round(performance.now() - metricsStart),
        status,
        errorCode,
        isFallback: false,
      });
    };

    while (retryCount < maxRetries) {
      try {
        const fetchStart = performance.now();
        const response = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': this.claudeAiKey || ANTHROPIC_API_KEY,
            'anthropic-version': '2023-06-01',
            'anthropic-beta': 'prompt-caching-2024-07-31',
            'anthropic-dangerous-direct-browser-access': 'true',
          },
          body: JSON.stringify({
            model: model,
            max_tokens: 1024,
            ...(systemBlocks.length > 0 ? { system: systemBlocks } : {}),
            messages: convoMsgs,
            stream: true,
          }),
          signal: abortSignal,
        });
        metricsTtfb = performance.now() - fetchStart;

        if (!response.ok) {
          throw { message: `API responded with status ${response.status}`, status: response.status, code: String(response.status), headers: response.headers };
        }

        if (!response.body) {
          throw new Error('No response body returned from Claude API');
        }
        console.log('Claude streaming response started', response);
        const reader = response.body.getReader();
        let fullText = '';
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value);
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed.startsWith('data:')) continue;

            const jsonStr = trimmed.replace('data:', '').trim();
            if (jsonStr === '[DONE]') {
              recordMetric('success');
              chatService.finalizeConversation();
              return;
            }

            try {
              const parsed = JSON.parse(jsonStr);
              if (parsed.type === 'content_block_delta') {
                const delta = parsed.delta?.text;
                if (delta) {
                  if (!metricsFirstToken && delta.trim()) {
                    metricsFirstToken = performance.now() - metricsStart;
                  }
                  fullText += delta;
                  chatService.updateBotMessage(botMessageIndex, fullText);
                  if (onUpdate) onUpdate(fullText);
                }
              }
            } catch (e) {
              console.error('Error parsing response data:', e);
            }
          }
        }

        recordMetric('success');
        chatService.finalizeConversation();
        break;

      } catch (err) {
        // If the provided abortSignal was triggered by the user (clicking stop),
        // prefer to short-circuit immediately and avoid retry logic.
        if (abortSignal?.aborted) {
          recordMetric('aborted');
          chatService.finalizeConversation();
          return;
        }
        const anyErr = err as any;
        if (anyErr?.name === 'AbortError' || anyErr?.code === 20 || anyErr?.type === 'aborted') {
          recordMetric('aborted');
          chatService.finalizeConversation();
          return;
        }

        console.log('Claude stream error encountered:', err);
        retryCount++;
        console.log(`Claude retry attempt ${retryCount} of ${maxRetries}`);

        // After the first failed attempt, check status: if inactive, immediate fallback
        // if (retryCount === 1) {
        //   try {
        //     const inactiveNow = await hasInactiveStatus('claude');
        //     if (inactiveNow) {
        //       // immediate fallback to OpenAI
        //       try {
        //         await this.streamAskQuestionWithContext(context, botMessageIndex, chatService, onUpdate, abortSignal, 'gpt-5.2', interviewType);
        //       } catch (fallbackErr) {
        //         const anyErr = err as any;
        //         if (anyErr?.name === 'AbortError' || anyErr?.code === 20 || anyErr?.type === 'aborted') {
        //           chatService.finalizeConversation();
        //           return;
        //         }
        //         console.error('Fallback to OpenAI failed after detecting inactive status (Claude):', fallbackErr);
        //         chatService.updateBotMessage(botMessageIndex, 'Error: Could not reach either Claude or OpenAI service');
        //         chatService.finalizeConversation();
        //         return;
        //       }
        //       chatService.finalizeConversation();
        //       return;
        //     }
        //   } catch (e) {
        //     // ignore status-check errors
        //   }
        // }

        // If not last attempt, show retry message and wait then retry
        if (retryCount < maxRetries) {
          chatService.updateBotMessage(botMessageIndex, 'Thinking...');
          await new Promise(res => setTimeout(res, 1000));
          continue;
        }

        // Final failure: record and notify, then fallback
        const extractedError = (() => {
          if (err && typeof err === 'object') {
            const anyErr = err as any;
            const statusFromErr = anyErr?.status ?? anyErr?.statusCode ?? anyErr?.response?.status
              ?? ((anyErr?.headers && typeof anyErr.headers.get === 'function') ? (anyErr.headers.get('status') || anyErr.headers.get('x-status-code')) : anyErr?.headers ?? undefined);
            return {
              code: anyErr?.code ?? String(statusFromErr ?? 'UNKNOWN_ERROR'),
              message: anyErr?.message ?? String(anyErr) ?? 'Unknown error during Claude request',
              status: statusFromErr
            } as any;
          }
          return { code: 'UNKNOWN_ERROR', message: String(err) || 'Unknown error during Claude request' } as any;
        })();

        await recordErrorAndNotify(extractedError.code, extractedError.message, extractedError.status);
        recordMetric('error', extractedError.code);

        // Fallback to OpenAI if configured
        if (interviewType === 'finalround') {
          try {
            await this.streamAskQuestionWithContext(context, botMessageIndex, chatService, onUpdate, abortSignal, 'gpt-5.2', interviewType, true, promptId);
            return;
          } catch (fallbackError) {
            const anyErr = err as any;
            if (anyErr?.name === 'AbortError' || anyErr?.code === 20 || anyErr?.type === 'aborted') {
              chatService.finalizeConversation();
              return;
            }
            console.error('Fallback to OpenAI failed after Claude error and marking inactive:', fallbackError);
            chatService.updateBotMessage(botMessageIndex, 'Sorry, Otter AI Assistant is currently unavailable. Please try again later.');
            chatService.finalizeConversation();
            return;
          }
        } else {
          chatService.updateBotMessage(botMessageIndex, 'Sorry, Otter AI Assistant is currently unavailable. Please try again later.');
          chatService.finalizeConversation();
          return;
        }
      }
    }
  }

  /**
   * Upload base64 image/file to backend and get S3 URL
   */
  uploadBase64File(request: { base64Content: string, fileExtension?: string }): Observable<string> {
    // Default fileExtension to .jpg if not provided
    const body = {
      base64Content: request.base64Content,
      fileExtension: request.fileExtension || '.jpg'
    };
    return this.http.post<string>(`${API_URL}/uploadBase64`, body);
  }

  /**
   * Upload text file to backend and get S3 URL
   */
  uploadTextFile(request: { textContent: string, fileName: string }): Observable<string> {
    return this.http.post<string>(`${API_URL}/uploadText`, request);
  }
  // Expected backend endpoints:
  // GET /api/prompts/:promptId -> { prompt: string }
  // GET /api/chat-history?promptId=... -> [{ question, answer, timestamp }]
  // POST /api/openai-stream { messages: [...] } -> streams OpenAI response
  // GET /api/ask?promptId=...&question=...&answer=... -> saves answer
  /**
   * Stream OpenAI answer using built context from frontend
   */
  // async streamAskQuestionWithContext(
  //   context: Array<{ role: string; content: string }>,
  //   botMessageIndex: number,
  //   chatService: any,
  //   onUpdate?: (partial: string) => void,
  //   abortSignal?: AbortSignal,
  //   model: string = 'gpt-4o',
  //   interviewType?: string
  // ): Promise<void> {
  //   const openai = new OpenAI({
  //     apiKey: OPENAI_API_KEY,
  //     dangerouslyAllowBrowser: true,
  //     timeout: 15000, // 15 second timeout
  //     maxRetries: 2,
  //   });

  //   let fullText = '';

  //   try {
  //     // Optimize input text construction
  //     const inputText = context.reduce((acc, m) => acc + m.content + '\n', '');

  //     const stream = await openai.responses.create(
  //       {
  //         model,
  //         input: inputText,
  //         stream: true,
  //         temperature: 0.1,
  //       },
  //       { signal: abortSignal }
  //     );

  //     for await (const event of stream) {
  //       if (event.type === 'response.output_text.delta' && typeof event.delta === 'string') {
  //         fullText += event.delta;
  //         chatService.updateBotMessage(botMessageIndex, fullText);
  //         if (onUpdate) onUpdate(fullText);
  //       }
  //     }
  //   } catch (err) {
  //     console.error('OpenAI Stream Error:', err);

  //     // Fallback logic if OpenAI fails
  //     try {
  //       if (interviewType === 'firstround') {
  //         // Try Claude Sonnet 4.5 as a fallback
  //         await this.streamClaudeAnswerWithContext(
  //           context,
  //           botMessageIndex,
  //           chatService,
  //           onUpdate,
  //           abortSignal,
  //           'claude-sonnet-4-5-20250929',
  //           interviewType
  //         );
  //         return;
  //       }

  //       if (interviewType === 'practice' || interviewType === 'screening') {
  //         // Try older Claude 3.7 as a fallback
  //         // Try Claude Sonnet 4.5 as a fallback
  //         await this.streamClaudeAnswerWithContext(
  //           context,
  //           botMessageIndex,
  //           chatService,
  //           onUpdate,
  //           abortSignal,
  //           'claude-sonnet-4-5-20250929',
  //           interviewType
  //         );
  //         return;
  //       }
  //     } catch (fallbackErr) {
  //       console.error('Fallback attempt after OpenAI failure also failed:', fallbackErr);
  //       // If fallback also fails, inform the user
  //       chatService.updateBotMessage(botMessageIndex, 'Error: Could not reach Claude or OpenAI services');
  //     }

  //     chatService.updateBotMessage(botMessageIndex, 'Error: Could not process the response');
  //   } finally {
  //     chatService.finalizeConversation();
  //   }
  // }

  async streamAskQuestionWithContext(
    context: Array<{ role: string; content: string }>,
    botMessageIndex: number,
    chatService: any,
    onUpdate?: (partial: string) => void,
    abortSignal?: AbortSignal,
    model: string = 'gpt-4o',
    interviewType?: string
    , skipStatusCheck: boolean = false,
    promptId?: string
  ): Promise<void> {
    // ── Sticky fallback: if OpenAI failed recently, skip it and go directly to Vertex AI Claude ──
    if (this._openAiFallbackTimestamp > 0) {
      const elapsed = Date.now() - this._openAiFallbackTimestamp;
      if (elapsed < this._stickyFallbackDurationMs) {
        console.log(`[Sticky Fallback] OpenAI failed ${Math.round(elapsed / 60000)}min ago — routing directly to Vertex AI Claude`);
        try {
          this.openAiService.warmGcpToken();
          await this.openAiService.streamVertexAIClaudeAnswerWithContext(
            context, botMessageIndex, chatService, onUpdate, abortSignal, 'anthropic-claude-sonnet-4-6', interviewType, undefined, promptId
          );
          return;
        } catch (e: any) {
          if (e?.name === 'AbortError' || abortSignal?.aborted) {
            chatService.finalizeConversation();
            return;
          }
          console.error('[Sticky Fallback] Vertex AI Claude also failed, resetting fallback to retry OpenAI:', e);
          this._openAiFallbackTimestamp = 0;
          // fall through to try OpenAI below
        }
      } else {
        console.log('[Sticky Fallback] Expired after 8 hours, resetting — will try OpenAI again');
        this._openAiFallbackTimestamp = 0;
      }
    }

    if (!skipStatusCheck) {
      try {
        const statuses = this.serviceStatus;
        const svc = (s: any) => String(s?.aiService || s?.aiservice || '').toLowerCase();
        const openaiInactive = statuses.some((s: any) => svc(s) === 'openai' && String(s.status).toLowerCase() === 'inactive');
        if (openaiInactive) {
          const claudeInactive = statuses.some((s: any) => svc(s) === 'claude' && String(s.status).toLowerCase() === 'inactive');
          if (claudeInactive) {
            chatService.updateBotMessage(botMessageIndex, 'Sorry, Otter AI Assistant is currently unavailable. Please try again later.');
            chatService.finalizeConversation();
            return;
          }
          // route to Vertex AI Claude fallback
          try {
            this.openAiService.warmGcpToken();
            await this.openAiService.streamVertexAIClaudeAnswerWithContext(
              context, botMessageIndex, chatService, onUpdate, abortSignal, 'anthropic-claude-sonnet-4-6', interviewType, undefined, promptId
            );
            this._openAiFallbackTimestamp = Date.now(); // activate sticky fallback
            return;
          } catch (e) {
            console.error('Immediate fallback to Vertex AI Claude failed (OpenAI inactive):', e);
            chatService.updateBotMessage(botMessageIndex, 'Sorry, Otter AI Assistant is currently unavailable. Please try again later.');
            chatService.finalizeConversation();
            return;
          }
        }
      } catch (e) {
        console.warn('Could not check OpenAI/Claude status before request, proceeding with OpenAI attempt', e);
      }
    }

    let retryCount = 0;
    const maxRetries = 3;
    let fullText = '';


    // const hasInactiveStatus = async (serviceName?: string): Promise<boolean> => {
    //   try {
    //     // Ensure stale inactive records are refreshed to active if past threshold
    //     await this.refreshAiKeyStatusesIfNeeded(480);
    //     const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
    //     const safeStatuses = Array.isArray(statuses) ? statuses : [];
    //     return safeStatuses.some((status) => {
    //       const isInactive = String(status?.status).toLowerCase() === 'inactive';
    //       if (!isInactive) return false;
    //       if (!serviceName) return true;
    //       const svc = String(status?.aiService || status?.aiservice || '').toLowerCase();
    //       return svc === serviceName.toLowerCase();
    //     });
    //   } catch (e) {
    //     return false;
    //   }
    // };

    const recordErrorAndNotify = async (errorCode: string, errorMessage: string, statusCode?: string | number) => {
      await this.upsertAiKeyStatus('openai', errorCode, errorMessage, statusCode);
      await this.upsertAiKeysStatus('claude', errorCode, errorMessage, statusCode);
    };

    // Build structured input array for OpenAI Responses API (preserves roles)
    const gptInput: any[] = [];
    for (const msg of context) {
      if (msg.role === 'system') {
        gptInput.push({ role: 'system', content: msg.content });
      } else if (msg.role === 'user') {
        gptInput.push({ role: 'user', content: [{ type: 'input_text', text: msg.content }] });
      } else if (msg.role === 'assistant') {
        gptInput.push({ role: 'assistant', content: [{ type: 'output_text', text: msg.content }] });
      }
    }

    while (retryCount < maxRetries) {
      const oaiMetricsStart = performance.now();
      let oaiTtfb = 0;
      let oaiFirstToken = 0;
      let oaiMetricsRecorded = false;

      const oaiUserQuestion = [...context].reverse().find(m => m.role === 'user')?.content || '';
      const recordOaiMetric = (status: string, errorCode?: string) => {
        if (oaiMetricsRecorded) return;
        oaiMetricsRecorded = true;
        this.metricsService.record({
          provider: 'openai',
          model,
          interviewType: interviewType || '',
          promptId: promptId || '',
          question: oaiUserQuestion,
          ttfbMs: Math.round(oaiTtfb),
          firstTokenMs: Math.round(oaiFirstToken),
          totalDurationMs: Math.round(performance.now() - oaiMetricsStart),
          status,
          errorCode,
          isFallback: false,
        });
      };

      try {
        const openai = new OpenAI({
          apiKey: this.openAiKey || OPENAI_API_KEY,
          dangerouslyAllowBrowser: true,
          timeout: 15000,
          maxRetries: 0,
        });

        const stream = await openai.responses.create(
          {
            model,
            input: gptInput,
            stream: true,
            temperature: 0.5,
          },
          { signal: abortSignal }
        );
        oaiTtfb = performance.now() - oaiMetricsStart;
        fullText = '';
        for await (const event of stream) {
          if (event.type === 'response.output_text.delta' && typeof event.delta === 'string') {
            if (!oaiFirstToken && event.delta.trim()) {
              oaiFirstToken = performance.now() - oaiMetricsStart;
            }
            fullText += event.delta;
            chatService.updateBotMessage(botMessageIndex, fullText);
            if (onUpdate) onUpdate(fullText);
          }
        }

        recordOaiMetric('success');
        break; // success

      } catch (err) {
        // If the caller's abortSignal has been triggered (user pressed stop),
        // short-circuit and do not run retry/fallback logic.
        if (abortSignal?.aborted) {
          recordOaiMetric('aborted');
          chatService.finalizeConversation();
          return;
        }
        const anyErr = err as any;
        const isAbortError = anyErr?.name === 'AbortError' || anyErr?.code === 20 || anyErr?.type === 'aborted';
        if (isAbortError) {
          recordOaiMetric('aborted');
          chatService.finalizeConversation();
          return;
        }

        retryCount++;

        // After the first failed attempt, check current status: if inactive, immediately fallback
        // if (retryCount === 1) {
        //   console.log('Checking OpenAI inactive status after first failure');
        //   try {
        //     const inactiveNow = await hasInactiveStatus('openai');
        //     if (inactiveNow) {
        //       console.log('OpenAI marked inactive, attempting immediate fallback to Claude');
        //       // immediate fallback to Claude variants depending on interviewType
        //       let fallbackModel = '';
        //       if (interviewType === 'firstround') {
        //         fallbackModel = 'claude-sonnet-4-5-20250929';
        //       } else if (interviewType === 'practice' || interviewType === 'screening') {
        //         fallbackModel = 'claude-sonnet-4-5-20250929';
        //       }
        //       if (fallbackModel) {
        //         try {
        //           await this.streamClaudeAnswerWithContext(context, botMessageIndex, chatService, onUpdate, abortSignal, fallbackModel, interviewType);
        //         } catch (fallbackErr) {
        //           const anyErr = err as any;
        //           if (anyErr?.name === 'AbortError' || anyErr?.code === 20 || anyErr?.type === 'aborted') {
        //             chatService.finalizeConversation();
        //             return;
        //           }
        //           console.error('Fallback to Claude failed after detecting inactive status:', fallbackErr);
        //         }
        //         chatService.finalizeConversation();
        //         return;
        //       }
        //     }
        //   } catch (e) {
        //     // ignore status-check errors and proceed with retries
        //   }
        // }

        if (retryCount < maxRetries) {
          chatService.updateBotMessage(botMessageIndex, 'Thinking...');
          await new Promise(res => setTimeout(res, 1000));
          continue;
        }

        const extractedError = (() => {
          if (err && typeof err === 'object') {
            const anyErr = err as any;
            const statusFromErr = anyErr?.status ?? anyErr?.statusCode ?? anyErr?.response?.status
              ?? ((anyErr?.headers && typeof anyErr.headers.get === 'function') ? (anyErr.headers.get('status') || anyErr.headers.get('x-status-code')) : anyErr?.headers ?? undefined);
            return {
              code: anyErr?.code ?? String(statusFromErr ?? 'UNKNOWN_ERROR'),
              message: anyErr?.message ?? String(anyErr) ?? 'Unknown error during OpenAI request',
              status: statusFromErr
            } as any;
          }
          return { code: 'UNKNOWN_ERROR', message: String(err) || 'Unknown error during OpenAI request' } as any;
        })();

        await recordErrorAndNotify(extractedError.code, extractedError.message, extractedError.status);
        recordOaiMetric('error', extractedError.code);

        // attempt fallback to Vertex AI Claude
        if (interviewType === 'practice' || interviewType === 'screening' || interviewType === 'firstround' || interviewType === 'finalround') {
          try {
            console.log(`[OpenAI] Failed after retries, falling back to Vertex AI Claude for ${interviewType}`);
            this.openAiService.warmGcpToken();
            await this.openAiService.streamVertexAIClaudeAnswerWithContext(
              context, botMessageIndex, chatService, onUpdate, abortSignal, 'anthropic-claude-sonnet-4-6', interviewType, undefined, promptId
            );
            this._openAiFallbackTimestamp = Date.now(); // activate sticky fallback for subsequent questions
            console.log('[Sticky Fallback] Activated — subsequent questions will go directly to Vertex AI Claude');
            return;
          } catch (fallbackErr: any) {
            if (fallbackErr?.name === 'AbortError' || abortSignal?.aborted) {
              chatService.finalizeConversation();
              return;
            }
            console.error('Fallback to Vertex AI Claude failed after OpenAI error:', fallbackErr);
          }
        }

        chatService.updateBotMessage(botMessageIndex, 'Error: Could not process the response after multiple retries.');
          chatService.updateBotMessage(botMessageIndex, 'Sorry, we could not process your request at this time. Please try again later.');
        chatService.finalizeConversation();
        return;
      }
    }
  }


  /**
   * Build context for OpenAI streaming: fetch prompt and chat history from backend, assemble context in frontend
   */
  async buildContext(promptId: string, question: string): Promise<Array<{ role: string; content: string }>> {
    // 1. Fetch system prompt using HttpClient
    const promptData: any = await this.http.get(`${API_URL}/prompt/${promptId}`).toPromise();
    const context: Array<{ role: string; content: string }> = [];
    const identityGuardrail = '\n\nIMPORTANT: You are Otter AI, an AI assistant built by Otter. You are NOT ChatGPT, not made by OpenAI, and not Claude. Never claim to be any other AI. If asked who you are or who made you, always say you are Otter AI.\n\nTENSE: If the question is about past experience, answer in past tense using real projects. If it is a hypothetical problem, answer in future tense — like "I would do this," "we can resolve it by…" — as if solving it in real time.\n\nNUMBER VARIETY: Never repeat the same numbers across different answers. Vary your metrics — different data sizes, latencies, and counts for each distinct scenario.';
    if (promptData && promptData.prompt) {
      context.push({ role: 'system', content: promptData.prompt + identityGuardrail });
    }
    // 2. Fetch chat history using HttpClient
    const historyData: any[] = await this.http.get<any[]>(`${API_URL}/chathistory/${encodeURIComponent(promptId)}`).toPromise() || [];
    // 3. Sort by timestamp descending, take last 2 messages
    const sorted = (historyData || []).sort((a: any, b: any) => Number(b.timestamp) - Number(a.timestamp));
    let count = 0;
    for (const chat of sorted) {
      if (count === 2) break;
      if (chat.question) {
        context.push({ role: 'user', content: chat.question });
      }
      if (chat.answer) {
        context.push({ role: 'assistant', content: chat.answer });
      }
      count++;
    }
    // 4. Add current user question
    context.push({ role: 'user', content: question });
    return context;
  }
  async ClaudeBuildContext(promptId: string, question: string): Promise<Array<{ role: string; content: string }>> {
    // 1. Fetch system prompt using HttpClient
    const promptData: any = await this.http.get(`${API_URL}/prompt/${promptId}`).toPromise();
    const context: Array<{ role: string; content: string }> = [];
    const identityGuardrail = '\n\nIMPORTANT: You are Otter AI, an AI assistant built by Otter. You are NOT ChatGPT, not made by OpenAI, and not Claude. Never claim to be any other AI. If asked who you are or who made you, always say you are Otter AI.\n\nTENSE: If the question is about past experience, answer in past tense using real projects. If it is a hypothetical problem, answer in future tense — like "I would do this," "we can resolve it by…" — as if solving it in real time.\n\nNUMBER VARIETY: Never repeat the same numbers across different answers. Vary your metrics — different data sizes, latencies, and counts for each distinct scenario.';
    // System prompt goes as 'system' role so Vertex AI sends it in the system attribute with cache_control
    if (promptData && promptData.prompt) {
      context.push({ role: 'system', content: promptData.prompt + identityGuardrail });
    }
    // 2. Fetch chat history using HttpClient
    const historyData: any[] = await this.http.get<any[]>(`${API_URL}/chathistory/${encodeURIComponent(promptId)}`).toPromise() || [];
    // 3. Sort by timestamp ascending, take last 3-4 Q&A pairs
    const sorted = (historyData || []).sort((a: any, b: any) => Number(a.timestamp) - Number(b.timestamp));
    let pairs = 0;
    for (const chat of sorted.reverse()) {
      if (pairs === 4) break;
      if (chat.question) {
        context.push({ role: 'user', content: chat.question });
      }
      if (chat.answer) {
        context.push({ role: 'assistant', content: chat.answer });
      }
      pairs++;
    }
    // 4. Add current user question
    context.push({ role: 'user', content: question });
    return context;
  }


  async buildContextFromPreloadedData(promptData: any, question: string, promptId: string): Promise<Array<{ role: string; content: string }>> {
    const context: Array<{ role: string; content: string }> = [];
    const identityGuardrail = '\n\nIMPORTANT: You are Otter AI, an AI assistant built by Otter. You are NOT ChatGPT, not made by OpenAI, and not Claude. Never claim to be any other AI. If asked who you are or who made you, always say you are Otter AI.\n\nTENSE: If the question is about past experience, answer in past tense using real projects. If it is a hypothetical problem, answer in future tense — like "I would do this," "we can resolve it by…" — as if solving it in real time.\n\nNUMBER VARIETY: Never repeat the same numbers across different answers. Vary your metrics — different data sizes, latencies, and counts for each distinct scenario.';

    // System prompt goes as 'system' role so Vertex AI sends it in the system attribute with cache_control
    if (promptData && promptData.prompt) {
      context.push({ role: 'system', content: promptData.prompt + identityGuardrail });
    }

    // Fetch chat history for the given promptId (service is responsible for history retrieval)
    const historyData: any[] = await this.http.get<any[]>(`${API_URL}/chathistory/${encodeURIComponent(promptId)}`).toPromise() || [];

    // Sort by timestamp ascending (oldest first) using Date parsing to be robust
    const sorted = (historyData || []).sort((a: any, b: any) => (new Date(a.timestamp).getTime() || 0) - (new Date(b.timestamp).getTime() || 0));

    // Include up to the last 4 Q/A pairs, but push them in oldest->newest order
    const maxPairs = 4;
    const startIndex = Math.max(0, sorted.length - maxPairs);
    for (let i = startIndex; i < sorted.length; i++) {
      const chat = sorted[i];
      if (chat.question) {
        context.push({ role: 'user', content: chat.question });
      }
      if (chat.answer) {
        context.push({ role: 'assistant', content: chat.answer });
      }
    }

    // Add current user question
    context.push({ role: 'user', content: question });
    return context;
  }
  /**
   * Build context from UI messages already in memory (no HTTP call).
   * contextMode 'ise': system prompt + ISE JSON state + last 1 Q/A + new question (compact, fast)
   * contextMode 'raw': system prompt + last 4 Q/A pairs + new question (full history fallback)
   */
  buildContextFromUiMessages(
    promptData: any,
    question: string,
    uiMessages: Message[],
    contextMode: 'ise' | 'raw' = 'ise'
  ): Array<{ role: string; content: string }> {
    const context: Array<{ role: string; content: string }> = [];
    const identityGuardrail = '\n\nIMPORTANT: You are Otter AI, an AI assistant built by Otter. You are NOT ChatGPT, not made by OpenAI, and not Claude. Never claim to be any other AI. If asked who you are or who made you, always say you are Otter AI.\n\nTENSE: If the question is about past experience, answer in past tense using real projects. If it is a hypothetical problem, answer in future tense — like "I would do this," "we can resolve it by…" — as if solving it in real time.\n\nNUMBER VARIETY: Never repeat the same numbers across different answers. Vary your metrics — different data sizes, latencies, and counts for each distinct scenario.';

    if (promptData && promptData.prompt) {
      context.push({ role: 'system', content: promptData.prompt + identityGuardrail });
    }

    // Collect Q/A pairs from in-memory UI messages (oldest→newest).
    // Walk backwards: expect alternating bot then user (newest→oldest).
    // Skip user messages >800 chars plain text (corrupted data from prior bug).
    const maxPairs = contextMode === 'ise' ? 1 : 4;
    const pairs: Array<{ role: string; content: string }> = [];
    let pairCount = 0;
    let pendingBot: string | null = null;
    for (let i = uiMessages.length - 1; i >= 0 && pairCount < maxPairs; i--) {
      const m = uiMessages[i];
      if (m.sender === 'bot' && m.text) {
        pendingBot = m.text;
      } else if (m.sender === 'user' && m.text) {
        const plainLen = m.text.replace(/<[^>]*>/g, '').length;
        if (plainLen > 800) {
          pendingBot = null;
          continue;
        }
        if (pendingBot !== null) {
          pairs.unshift({ role: 'assistant', content: pendingBot });
          pairs.unshift({ role: 'user', content: m.text });
          pairCount++;
          pendingBot = null;
        }
      }
    }

    // ISE mode: inject structured state JSON before the last Q/A pair
    if (contextMode === 'ise') {
      const stateStr = JSON.stringify(this._stateJSON);
      if (stateStr !== JSON.stringify(EMPTY_STATE)) {
        context.push({
          role: 'system',
          content: `Conversation state (use "topic" to maintain context for follow-ups — e.g. if topic is "Java" and user says "write two programs", write Java programs. But if the user asks about a completely different topic, answer fresh without mixing old content. If "pending" is non-empty, the previous explanation was incomplete — continue from where it left off when asked. "artifacts" lists code/diagrams already shared — do not repeat them):\n${stateStr}`
        });
        console.log(`[ISE] State injected (${stateStr.length} chars)`);
      }
    }

    context.push(...pairs);
    context.push({ role: 'user', content: question });
    console.log(`[Context] Mode: ${contextMode.toUpperCase()} | Messages: ${context.length} | Total chars: ${context.reduce((s, m) => s + m.content.length, 0)}`);
    return context;
  }

  /**
   * Save answer to backend after streaming is complete
   */
async saveAnswer(
  promptId: string,
  question: string,
  answer: string,
  s3Url?: string
): Promise<any> {
  let url = `${API_URL}/ask`;
  const params = new URLSearchParams({ promptId });

  if (s3Url) {
    params.append('s3Url', s3Url);
  }

  url += `?${params.toString()}`;

  const body = {
    question,
    answer
  };

  try {
    // Convert Observable to Promise so callers using `await` will actually execute and wait
    const resp = await firstValueFrom(this.http.post(url, body));
    return resp;
  } catch (err) {
    console.error('saveAnswer failed', err);
    throw err;
  }
}



  /**
   * Send both text and file URL to OpenAI /v1/responses endpoint using fetch
   */
  sendTextAndFileToOpenAI(inputText: string, fileUrl: string): Observable<any> {
    const openAiApiKey =  this.openAiKey || OPENAI_API_KEY;
    const body = {
      model: 'gpt-5.2',
      input: [
        {
          role: 'user',
          content: [
            { type: 'input_text', text: inputText },
            { type: 'input_file', file_url: fileUrl }
          ]
        }
      ]
    };

    return new Observable(observer => {
      fetch('https://api.openai.com/v1/responses', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${openAiApiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then(data => {
          observer.next(data);
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  /**
   * Send both text and image URL to OpenAI /v1/responses endpoint using fetch
   */
  private openAiKeyLoad?: Promise<string>;

  /**
   * Returns the OpenAI key, loading and decrypting it from the backend when the page has not finished
   * that yet (keys arrive asynchronously after load, so an early request must not go out with an empty bearer).
   */
  async ensureOpenAiKey(): Promise<string> {
    if (this.openAiKey) return this.openAiKey;
    if (OPENAI_API_KEY) return OPENAI_API_KEY;
    if (!this.openAiKeyLoad) {
      this.openAiKeyLoad = (async () => {
        const keys = await firstValueFrom(this.getApiKeys());
        const { cryptoKey, fallbackKeyBytes } = await importAesKey(AES_SECRET_KEY);
        const openAiKey = keys?.openAiKey ? await decryptKey(keys.openAiKey, cryptoKey, fallbackKeyBytes) : '';
        const claudeAiKey = keys?.claudeAiKey ? await decryptKey(keys.claudeAiKey, cryptoKey, fallbackKeyBytes) : '';
        if (openAiKey) this.setKeys(openAiKey, claudeAiKey || this.claudeAiKey);
        return openAiKey;
      })().finally(() => { this.openAiKeyLoad = undefined; });
    }
    return this.openAiKeyLoad;
  }

  /** The ISE conversation state (topic, artifacts, pending work) or null while it is still empty. */
  getConversationState(): ConversationState | null {
    return JSON.stringify(this._stateJSON) === JSON.stringify(EMPTY_STATE) ? null : this._stateJSON;
  }

  /**
   * Streams one Responses API call and reports the output text as it grows. Resolves with the complete text.
   * Used by coding mode so the answer appears within about a second instead of after the whole reply.
   */
  async streamOpenAIResponse(
    input: unknown[],
    options: { model: string; textFormat?: Record<string, unknown>; reasoningEffort?: string; maxOutputTokens?: number },
    onText?: (text: string) => void,
    signal?: AbortSignal
  ): Promise<string> {
    const apiKey = await this.ensureOpenAiKey().catch(error => {
      console.error('[OpenAI] Could not load the API key', error);
      return '';
    });
    if (!apiKey) throw new Error('OpenAI API key is not available');
    const body: Record<string, unknown> = { model: options.model, input, stream: true };
    if (options.textFormat) body['text'] = { format: options.textFormat };
    if (options.reasoningEffort) body['reasoning'] = { effort: options.reasoningEffort };
    if (options.maxOutputTokens) body['max_output_tokens'] = options.maxOutputTokens;

    const response = await fetch('https://api.openai.com/v1/responses', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal
    });
    if (!response.ok || !response.body) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let text = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data:')) continue;
        const data = trimmed.slice(5).trim();
        if (!data || data === '[DONE]') continue;
        let event: any;
        try {
          event = JSON.parse(data);
        } catch {
          continue;
        }
        if (event?.type === 'response.output_text.delta' && typeof event.delta === 'string') {
          text += event.delta;
          onText?.(text);
        } else if (event?.type === 'response.incomplete' || event?.type === 'response.failed' || event?.type === 'error') {
          throw new Error(`OpenAI stream ended early: ${event.type}`);
        }
      }
    }
    if (!text) throw new Error('OpenAI returned no output');
    return text;
  }

  sendTextAndImageToOpenAI(inputText: string, imageUrl: string,
      options?: { textFormat?: Record<string, unknown>; model?: string }): Observable<any> {
    return this.createOpenAIResponse([
      {
        role: 'user',
        content: [
          { type: 'input_text', text: inputText },
          ...(imageUrl ? [{ type: 'input_image', image_url: imageUrl }] : [])
        ]
      }
    ], options);
  }

  /**
   * One non-streamed call to the OpenAI Responses API with a full `input` list (developer/user/assistant
   * turns, text and images). Loads the API key on demand so an early call never goes out unauthenticated.
   */
  createOpenAIResponse(input: unknown[], options?: {
    textFormat?: Record<string, unknown>;
    model?: string;
    /** Reasoning effort for reasoning models (none, low, medium, high, xhigh, max). Omitted = model default. */
    reasoningEffort?: string;
    /** Caps reasoning + visible output together; reasoning models need generous headroom (25k+). */
    maxOutputTokens?: number;
  }): Observable<any> {
    const body: Record<string, unknown> = { model: options?.model || 'gpt-5.2', input };
    // Structured output (e.g. a strict JSON schema) when the caller needs a machine-validated reply.
    if (options?.textFormat) body['text'] = { format: options.textFormat };
    if (options?.reasoningEffort) body['reasoning'] = { effort: options.reasoningEffort };
    if (options?.maxOutputTokens) body['max_output_tokens'] = options.maxOutputTokens;

    return new Observable(observer => {
      this.ensureOpenAiKey()
        .catch(error => {
          console.error('[OpenAI] Could not load the API key', error);
          return '';
        })
        .then(openAiApiKey => fetch('https://api.openai.com/v1/responses', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${openAiApiKey}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        }))
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then(data => {
          observer.next(data);
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  getOpenApiData(messages: Message[]): Observable<string> {
    const payload = messages.map(msg => ({
      role: msg.sender,
      content: msg.text
    }));
    return this.http.post(`${API_URL}/generate-doc`, payload, { responseType: 'text' });
  }

  getSummary(messages: Message[]): Observable<SummaryResponse> {
    const payload = messages.map(msg => ({
      role: msg.sender,
      content: msg.text,
    }));
    return this.http.post<SummaryResponse>(`${API_URL}/summarize`, payload);
  }
  getAllPrompts(): Observable<PromptMemory[]> {
    return this.http.get<PromptMemory[]>(`${API_URL}/prompts`);
  }
  askQuestion(dto: QuestionDto): Observable<any> {
    return this.http.post(`${API_URL}/ask`, dto);
  }
  getPromptById(id: string): Observable<PromptMemory> {
    return this.http.get<PromptMemory>(`${API_URL}/prompt/${id}`);
  }
  updatePrompt(id: string, prompt: string): Observable<string> {
    return this.http.put(`${API_URL}/prompt/${id}`, { id, prompt }, { responseType: 'text' });
  }
  deletePrompt(id: string): Observable<string> {
    return this.http.delete(`${API_URL}/prompt/${id}`, { responseType: 'text' });
  }
  getAllChatHistory(chatId: string): Observable<ChatHistory[]> {
    return this.http.get<ChatHistory[]>(`${API_URL}/chathistory/${chatId}`);
  }
  getChatHistoryByThread(chatThreadId: string): Observable<ChatHistory[]> {
    return this.http.get<ChatHistory[]>(`${API_URL}/history/thread/${chatThreadId}`);
  }
  getChatHistoryByPromptId(promptId: string): Observable<ChatHistory[]> {
    return this.http.get<ChatHistory[]>(`${API_URL}/chathistory/${promptId}`);
  } 

  
  getUserChatHistoryByPromptId(promptId: string): Observable<ChatHistory[]> {
    return this.http.get<ChatHistory[]>(`${API_URL}/user/chathistory/${promptId}`);
  }
  deleteChatHistory(id: string): Observable<string> {
    return this.http.delete(`${API_URL}/history/${id}`, { responseType: 'text' });
  }
  getAiKeyStatus(): Observable<aiKeyStatusDTO[]> {
    return this.http.get<aiKeyStatusDTO[]>(`${API_URL}/aiKeyStatus/all`);
  }

  createAiKeyStatus(payload: aiKeyStatusDTO): Observable<aiKeyStatusDTO> {
    return this.http.post<aiKeyStatusDTO>(`${API_URL}/aiKeyStatus`, payload);
  }

  updateAiKeyStatus(id: string, payload: aiKeyStatusDTO): Observable<aiKeyStatusDTO> {
    return this.http.put<aiKeyStatusDTO>(`${API_URL}/aiKeyStatus/${id}`, payload);
  } 

  getApiKeys(): Observable<ApiKeys> {
    return this.http.get<ApiKeys>(`${API_URL}/keys`);
  }

  aikeyStatusEmailSend(errorCode: string, errorMessage: string, statusCode?: string | number, aiService?: string): Observable<string> {
    try {
      // Prefer explicit statusCode if provided (comes from response headers or error.status),
      // otherwise fall back to provided errorCode string.
      const codeStr = String(statusCode ?? errorCode ?? '');
      // Determine recipients: if 401 or 429 -> all configured emails; otherwise only Jagadish & Sarath
      const cfg: any = emails || {};
      const allEmails = Object.values(cfg).filter(Boolean) as string[];
      const jagadish = cfg['Jagadish'] || cfg['jagadish'] || '';
      const sarath = cfg['Sarath'] || cfg['sarath'] || '';

      let recipientsEmails: string[] = [];
      if (codeStr.includes('401') || codeStr.includes('429')) {
        recipientsEmails = allEmails;
      } else {
        recipientsEmails = [jagadish, sarath].filter(Boolean);
      }

      // Build userInfoList array matching the curl format: [{ email, name }, ...]
      const userInfoList = recipientsEmails.map(email => {
        // try to infer the configured key/name from the config map, otherwise fallback to empty name
        const nameKey = Object.keys(cfg).find(k => cfg[k] === email) || '';
        const name = nameKey || (email === jagadish ? 'Jagadish' : (email === sarath ? 'Sarath' : ''));
        return { email, name };
      });

      const body = {
        errorCode: codeStr,
        errorMessage: String(errorMessage || ''),
        userInfoList,
        aiService: aiService || ''
      };

      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      return this.http.post<string>(`${API_URL}/aiKeyStatus/check-api-key-status`, body, { headers });
    } catch (e) {
      // Fallback: if JSON build/send fails for any reason, fall back to form-url-encoded for robustness
      try {
        const cfg: any = emails || {};
        const jagadish = cfg['Jagadish'] || cfg['jagadish'] || '';
        const sarath = cfg['Sarath'] || cfg['sarath'] || '';
        let params = new HttpParams().set('errorCode', String(statusCode ?? errorCode ?? '')).set('errorMessage', String(errorMessage || ''));
        const fallRecipients = [jagadish, sarath].filter(Boolean);
        for (const r of fallRecipients) {
          params = params.append('emailAddresses[]', r);
        }
        const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
        return this.http.post<string>(`${API_URL}/aiKeyStatus/check-api-key-status`, params.toString(), { headers });
      } catch (e2) {
        // Last-resort: send as simple request params using HttpParams with string values
        const lastParams = new HttpParams()
          .set('errorCode', String(errorCode ?? ''))
          .set('errorMessage', String(errorMessage ?? ''))
          .set('aiService', String(aiService ?? ''));
        // send an empty body string to ensure response typing aligns with Observable<string>
        return this.http.post<string>(`${API_URL}/aiKeyStatus/check-api-key-status`, '', { params: lastParams });
      }
    }
  }

  /**
   * Refresh stored AI key statuses: if any record is inactive for >= thresholdMinutes,
   * mark it active again using the backend.
   */
  async refreshAiKeyStatusesIfNeeded(thresholdMinutes: number = 480): Promise<any> {
    try {
      const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
      const now = Date.now();
      this.serviceStatus = statuses;
      console.log('refreshAiKeyStatusesIfNeeded: fetched statuses', statuses);
      const thresholdMs = thresholdMinutes * 60 * 1000;
      for (const s of (statuses || [])) {
        try {
          if (s && String(s.status).toLowerCase() === 'inactive') {
            const created = Number(s.createdAT) || 0;
            if (now - created >= thresholdMs) {
              // Reactivating: mark active, clear any prior error message and refresh createdAT
              const updated: aiKeyStatusDTO = { ...s, status: 'active', error: '', createdAT: Date.now() } as any;
              await this.updateAiKeyStatus(s.id, updated).pipe(take(1)).toPromise();
              const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
              this.serviceStatus = statuses;
            }
          }
        } catch (e) {
          console.warn('refreshAiKeyStatusesIfNeeded: single record update failed', e);
        }
      }

      return Array.isArray(statuses) ? statuses : [];
    } catch (e) {
      console.warn('refreshAiKeyStatusesIfNeeded failed', e);
    }
  }

  /**
   * Public helper: refresh statuses (reactivate stale inactive records) and return the latest statuses.
   * Useful for callers that want to ensure statuses are up-to-date before using them.
   */
  async refreshAndGetAiKeyStatus(thresholdMinutes: number = 480): Promise<aiKeyStatusDTO[]> {
    try {
      const status = await this.refreshAiKeyStatusesIfNeeded(thresholdMinutes);
      // const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
      // return Array.isArray(statuses) ? statuses : [];
      return Array.isArray(status) ? status : [];
    } catch (e) {
      console.warn('refreshAndGetAiKeyStatus failed', e);
      return [];
    }
  }

  /**
   * Create or update an aiKeyStatus record for a given service and send notification.
   * If a record exists for the service, update it; otherwise create a new one.
   */
  private async upsertAiKeyStatus(serviceName: string, errorCode: string, errorMessage: string, statusCode?: string | number): Promise<void> {
    try {
      const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
      this.serviceStatus = statuses
      const safeStatuses = Array.isArray(statuses) ? statuses : [];
      const existing = safeStatuses.find((s: any) => String((s.aiService || s.aiservice || '')).toLowerCase() === serviceName.toLowerCase());
      const payload: aiKeyStatusDTO = {
        id: existing?.id || '',
        error: errorMessage,
        status: 'inactive',
        createdAT: Date.now(),
        aiService: serviceName
      } as any;

      if (existing && existing.id) {
        try {
          await this.updateAiKeyStatus(existing.id, payload).pipe(take(1)).toPromise();
          const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
          this.serviceStatus = statuses
        } catch (e) {
          console.warn('upsertAiKeyStatus update failed, trying create', e);
          await this.createAiKeyStatus(payload).pipe(take(1)).toPromise();
        }
      } else {
        await this.createAiKeyStatus(payload).pipe(take(1)).toPromise();
      }

      // send notification (best-effort). Pass statusCode if available so recipient logic can use it.
      try {
        await this.aikeyStatusEmailSend(errorCode, errorMessage, statusCode, serviceName).pipe(take(1)).toPromise();
      } catch (e) {
        console.warn('upsertAiKeyStatus: email send failed', e);
      }
    } catch (e) {
      console.warn('upsertAiKeyStatus failed', e);
    }
  }

  /**
   * Extracts the output text from OpenAI /v1/responses API response
   */
  extractOpenAIText(response: any): string {
    // Handles both file and image response format
    if (response && response.output && Array.isArray(response.output) && response.output.length > 0) {
      const msg = response.output[0];
      if (msg && msg.content && Array.isArray(msg.content)) {
        for (const item of msg.content) {
          if (item.type === 'output_text' && item.text) {
            return item.text;
          }
        }
      }
    }
    // fallback: try response.content.text (for legacy or direct format)
    if (response && response.content && response.content.text) {
      return response.content.text;
    }
    return '';
  } 

  setKeys(openAiKey: string, claudeAiKey: string) {
    this.openAiKey = openAiKey;
    this.claudeAiKey = claudeAiKey;
  } 


  // Overlay api 

  getChatHistory(promptId: string): Observable<ChatHistory[]> {
    return this.http.get<ChatHistory[]>(`${API_URL}/chat-history/${promptId}`);
  }  

  async saveAiAnswer(
  promptId: string,
  question: string,
  answer: string,
  s3Url?: string
): Promise<any> {
  let url = `${API_URL}/save-response`;
  const params = new URLSearchParams({ promptId });

  if (s3Url) {
    params.append('s3Url', s3Url);
  }

  url += `?${params.toString()}`;

  const body = {
    question,
    answer
  };

  try {
    // Convert Observable to Promise so callers using `await` will actually execute and wait
    const resp = await firstValueFrom(this.http.post(url, body));
    return resp;
  } catch (err) {
    console.error('saveAnswer failed', err);
    throw err;
  }
} 
 

 getAiKeysStatus(): Observable<aiKeyStatusDTO[]> {
    return this.http.get<aiKeyStatusDTO[]>(`${API_URL}/aiKeyStatus/keys/all`);
  }

  createAiKeysStatus(payload: aiKeyStatusDTO): Observable<aiKeyStatusDTO> {
    return this.http.post<aiKeyStatusDTO>(`${API_URL}/aiKeyStatus/keys`, payload);
  }

  updateAiKeysStatus(id: string, payload: aiKeyStatusDTO): Observable<aiKeyStatusDTO> {
    return this.http.put<aiKeyStatusDTO>(`${API_URL}/aiKeyStatus/keys/${id}`, payload);
  } 

  getApiKey(): Observable<ApiKeys> {
    return this.http.get<ApiKeys>(`${API_URL}/keys/ai`);
  }

  aikeysStatusEmailSend(errorCode: string, errorMessage: string, statusCode?: string | number, aiService?: string): Observable<string> {
    try {
      // Prefer explicit statusCode if provided (comes from response headers or error.status),
      // otherwise fall back to provided errorCode string.
      const codeStr = String(statusCode ?? errorCode ?? '');
      // Determine recipients: if 401 or 429 -> all configured emails; otherwise only Jagadish & Sarath
      const cfg: any = emails || {};
      const allEmails = Object.values(cfg).filter(Boolean) as string[];
      const jagadish = cfg['Jagadish'] || cfg['jagadish'] || '';
      const sarath = cfg['Sarath'] || cfg['sarath'] || '';

      let recipientsEmails: string[] = [];
      if (codeStr.includes('401') || codeStr.includes('429')) {
        recipientsEmails = allEmails;
      } else {
        recipientsEmails = [jagadish, sarath].filter(Boolean);
      }

      // Build userInfoList array matching the curl format: [{ email, name }, ...]
      const userInfoList = recipientsEmails.map(email => {
        // try to infer the configured key/name from the config map, otherwise fallback to empty name
        const nameKey = Object.keys(cfg).find(k => cfg[k] === email) || '';
        const name = nameKey || (email === jagadish ? 'Jagadish' : (email === sarath ? 'Sarath' : ''));
        return { email, name };
      });

      const body = {
        errorCode: codeStr,
        errorMessage: String(errorMessage || ''),
        userInfoList,
        aiService: aiService || ''
      };

      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      return this.http.post<string>(`${API_URL}/aiKeyStatus/keys/check-api-key-status`, body, { headers });
    } catch (e) {
      // Fallback: if JSON build/send fails for any reason, fall back to form-url-encoded for robustness
      try {
        const cfg: any = emails || {};
        const jagadish = cfg['Jagadish'] || cfg['jagadish'] || '';
        const sarath = cfg['Sarath'] || cfg['sarath'] || '';
        let params = new HttpParams().set('errorCode', String(statusCode ?? errorCode ?? '')).set('errorMessage', String(errorMessage || ''));
        const fallRecipients = [jagadish, sarath].filter(Boolean);
        for (const r of fallRecipients) {
          params = params.append('emailAddresses[]', r);
        }
        const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
        return this.http.post<string>(`${API_URL}/aiKeyStatus/keys/check-api-key-status`, params.toString(), { headers });
      } catch (e2) {
        // Last-resort: send as simple request params using HttpParams with string values
        const lastParams = new HttpParams()
          .set('errorCode', String(errorCode ?? ''))
          .set('errorMessage', String(errorMessage ?? ''))
          .set('aiService', String(aiService ?? ''));
        // send an empty body string to ensure response typing aligns with Observable<string>
        return this.http.post<string>(`${API_URL}/aiKeyStatus/keys/check-api-key-status`, '', { params: lastParams });
      }
    }
  }

  /**
   * Refresh stored AI key statuses: if any record is inactive for >= thresholdMinutes,
   * mark it active again using the backend.
   */
  async refreshAiKeysStatusesIfNeeded(thresholdMinutes: number = 480): Promise<any> {
    try {
      const statuses: any = await this.getAiKeysStatus().pipe(take(1)).toPromise();
      const now = Date.now();
      this.serviceStatus = statuses;
      console.log('refreshAiKeyStatusesIfNeeded: fetched statuses', statuses);
      const thresholdMs = thresholdMinutes * 60 * 1000;
      for (const s of (statuses || [])) {
        try {
          if (s && String(s.status).toLowerCase() === 'inactive') {
            const created = Number(s.createdAT) || 0;
            if (now - created >= thresholdMs) {
              // Reactivating: mark active, clear any prior error message and refresh createdAT
              const updated: aiKeyStatusDTO = { ...s, status: 'active', error: '', createdAT: Date.now() } as any;
              await this.updateAiKeysStatus(s.id, updated).pipe(take(1)).toPromise();
              const statuses: any = await this.getAiKeysStatus().pipe(take(1)).toPromise();
              this.serviceStatus = statuses;
            }
          }
        } catch (e) {
          console.warn('refreshAiKeysStatusesIfNeeded: single record update failed', e);
        }
      }

      return Array.isArray(statuses) ? statuses : [];
    } catch (e) {
      console.warn('refreshAiKeysStatusesIfNeeded failed', e);
    }
  }

  /**
   * Public helper: refresh statuses (reactivate stale inactive records) and return the latest statuses.
   * Useful for callers that want to ensure statuses are up-to-date before using them.
   */
  async refreshAndGetAiKeysStatus(thresholdMinutes: number = 480): Promise<aiKeyStatusDTO[]> {
    try {
      const status = await this.refreshAiKeysStatusesIfNeeded(thresholdMinutes);
      // const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
      // return Array.isArray(statuses) ? statuses : [];
      return Array.isArray(status) ? status : [];
    } catch (e) {
      console.warn('refreshAndGetAiKeysStatus failed', e);
      return [];
    }
  }

  /**
   * Create or update an aiKeyStatus record for a given service and send notification.
   * If a record exists for the service, update it; otherwise create a new one.
   */
  private async upsertAiKeysStatus(serviceName: string, errorCode: string, errorMessage: string, statusCode?: string | number): Promise<void> {
    try {
      const statuses: any = await this.getAiKeysStatus().pipe(take(1)).toPromise();
      this.serviceStatus = statuses
      const safeStatuses = Array.isArray(statuses) ? statuses : [];
      const existing = safeStatuses.find((s: any) => String((s.aiService || s.aiservice || '')).toLowerCase() === serviceName.toLowerCase());
      const payload: aiKeyStatusDTO = {
        id: existing?.id || '',
        error: errorMessage,
        status: 'inactive',
        createdAT: Date.now(),
        aiService: serviceName
      } as any;

      if (existing && existing.id) {
        try {
          await this.updateAiKeysStatus(existing.id, payload).pipe(take(1)).toPromise();
          const statuses: any = await this.getAiKeysStatus().pipe(take(1)).toPromise();
          this.serviceStatus = statuses
        } catch (e) {
          console.warn('upsertAiKeysStatus update failed, trying create', e);
          await this.createAiKeysStatus(payload).pipe(take(1)).toPromise();
        }
      } else {
        await this.createAiKeysStatus(payload).pipe(take(1)).toPromise();
      }

      // send notification (best-effort). Pass statusCode if available so recipient logic can use it.
      try {
        await this.aikeysStatusEmailSend(errorCode, errorMessage, statusCode, serviceName).pipe(take(1)).toPromise();
      } catch (e) {
        console.warn('upsertAiKeysStatus: email send failed', e);
      }
    } catch (e) {
      console.warn('upsertAiKeysStatus failed', e);
    }
  }

  /** Pre-warm the GCP token. Delegates to OpenAiService. */
  warmGcpToken(): void {
    this.openAiService.warmGcpToken();
  }

  /** Stream Vertex AI Claude answer. Delegates to OpenAiService. */
  async streamVertexAIClaudeAnswerWithContext(
    context: Array<{ role: string; content: string }>,
    botMessageIndex: number,
    chatService: any,
    onUpdate?: (partial: string) => void,
    abortSignal?: AbortSignal,
    modelKey?: string,
    interviewType?: string,
    region?: any,
    promptId?: string
  ): Promise<void> {
    return this.openAiService.streamVertexAIClaudeAnswerWithContext(
      context, botMessageIndex, chatService, onUpdate, abortSignal, modelKey, interviewType, region, promptId
    );
  }

}
